import { RouterProvider, createBrowserRouter } from "react-router-dom";

import { UserType } from "./types";

import ProtectedRoute from "./routes/protected-route";
import { LoginPage, ForgotPasswordPage } from "./routes/authentication";
import CalendarPage from "./routes/calendar/calendar-page";
import AboutPage from "./routes/about/about-page";
import ErrorPage from "./error";
import TeamsPage from "./routes/teams/teams-page";
import TeamPage from "./routes/team/team-page";
import AccountInfoPage from "./routes/account-info";
import PatientPage from "./routes/patient/patient-page";
import ClientPage from "./routes/client/client-page";
import PatientsPage from "./routes/patients/patients-page";
import UsersPage from "./routes/users";
import HelpPage from "./routes/help/help-page";
import ModelViewerPage from "./routes/model-viewer/model-viewer-page";
import AdminDashboardPage from "./routes/admin/admin-dashboard-page";
import MeetingConfirmationPage from "./routes/meeting/meeting-confirmation-page";
import MeetingRouterPage from "./routes/meeting/meeting-router-page";
import MeetingInvitePage from "./routes/meeting/meeting-invite-page";
import MeetingTimePage from "./routes/meeting/meeting-time-page";
import MeetingLeftPage from "./routes/meeting/meeting-left-page";

export enum RouteType {
  Empty = "/",
  Login = "/login",
  Dashboard = "/dashboard",
  Calendar = "/calendar",
  About = "/about",
  Account = "/account",
  Team = "/team",
  Teams = "/teams",
  Admin = "/admin",
  Client = "/client",
  Patient = "/patient",
  Patients = "/patients",
  Users = "/users",
  Help = "/help",
  ModelViewer = "/model-viewer",
  ForgotPassword = "/forgotpassword",
  MeetingLeft = "/meeting-left",
  MeetingConfirm = "/meeting/:meetingId/confirm",
  MeetingInvite = "/meeting/:meetingId/invite",
  MeetingTime = "/meeting/:meetingId/time",
  MeetingRouter = "/meeting",
  Error = "*",
}

const Router = () => {
  const browserRouter = createBrowserRouter(
    [
      {
        path: RouteType.Empty,
        element: (
          <ProtectedRoute
            authenticationPath={RouteType.Login}
            requiredUserLevel={UserType.User}
            outlet={<CalendarPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Dashboard,
        element: (
          <ProtectedRoute
            authenticationPath={RouteType.Login}
            requiredUserLevel={UserType.User}
            outlet={<CalendarPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Calendar,
        element: (
          <ProtectedRoute
            authenticationPath={RouteType.Login}
            requiredUserLevel={UserType.User}
            outlet={<CalendarPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Account,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<AccountInfoPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.About,
        element: <AboutPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Teams,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<TeamsPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Admin,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.DeveloperAdmin}
            authenticationPath={RouteType.Empty}
            outlet={<AdminDashboardPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Client,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.SysAdmin}
            authenticationPath={RouteType.Login}
            outlet={<ClientPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Team,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.OrgAdmin}
            authenticationPath={RouteType.Login}
            outlet={<TeamPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Patient,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<PatientPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Patients,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<PatientsPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Users,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<UsersPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Help,
        element: (
          <ProtectedRoute
            requiredUserLevel={UserType.User}
            authenticationPath={RouteType.Login}
            outlet={<HelpPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.ModelViewer,
        element: (
          <ProtectedRoute
            authenticationPath={RouteType.Login}
            requiredUserLevel={UserType.User}
            outlet={<ModelViewerPage />}
          />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Login,
        element: <LoginPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.ForgotPassword,
        element: <ForgotPasswordPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.MeetingLeft,
        element: <MeetingLeftPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.MeetingConfirm,
        element: <MeetingConfirmationPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.MeetingTime,
        element: <MeetingTimePage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.MeetingInvite,
        element: <MeetingInvitePage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.MeetingRouter,
        element: <MeetingRouterPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: RouteType.Error,
        element: <ErrorPage />,
        errorElement: <ErrorPage />,
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_startTransition: true,
        v7_skipActionErrorRevalidation: true,
      },
    },
  );

  return <RouterProvider router={browserRouter} />;
};

export default Router;
